import {
  useEffect,
  useMemo,
  useState,
} from "react";

import QRCode from "qrcode";

import {
  Check,
  Copy,
  Download,
  ExternalLink,
  Link2,
  QrCode,
  Share2,
  UtensilsCrossed,
  X,
} from "lucide-react";

type NovaCmsQrManagerProps = {
  onClose: () => void;
};

type QrTarget = {
  id: string;
  label: string;
  description: string;
  url: string;
  tableNumber: number | null;
};

function clampTableCount(
  value: number,
) {
  if (!Number.isFinite(value)) {
    return 1;
  }

  return Math.max(
    1,
    Math.min(
      100,
      Math.floor(value),
    ),
  );
}

function safeFilename(
  value: string,
) {
  return value
    .trim()
    .toLowerCase()
    .replace(
      /[^a-z0-9]+/g,
      "-",
    )
    .replace(
      /^-+|-+$/g,
      "",
    );
}

function downloadBlob(
  filename: string,
  blob: Blob,
) {
  const objectUrl =
    URL.createObjectURL(blob);

  const anchor =
    document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = filename;

  document.body.appendChild(
    anchor,
  );

  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(
    objectUrl,
  );
}

function downloadDataUrl(
  filename: string,
  dataUrl: string,
) {
  const anchor =
    document.createElement("a");

  anchor.href = dataUrl;
  anchor.download = filename;

  document.body.appendChild(
    anchor,
  );

  anchor.click();
  anchor.remove();
}

function QrPreview({
  target,
}: {
  target: QrTarget;
}) {
  const [
    dataUrl,
    setDataUrl,
  ] =
    useState("");

  const [
    busy,
    setBusy,
  ] =
    useState(false);

  const [
    copied,
    setCopied,
  ] =
    useState(false);

  const [
    message,
    setMessage,
  ] =
    useState("");

  useEffect(() => {
    let active = true;

    void QRCode.toDataURL(
      target.url,
      {
        width: 720,
        margin: 2,
        errorCorrectionLevel:
          "H",
        color: {
          dark: "#183d2a",
          light: "#fffdf4",
        },
      },
    )
      .then((nextUrl) => {
        if (active) {
          setDataUrl(
            nextUrl,
          );
        }
      })
      .catch(() => {
        if (active) {
          setMessage(
            "Could not generate QR preview.",
          );
        }
      });

    return () => {
      active = false;
    };
  }, [
    target.url,
  ]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(
        target.url,
      );

      setCopied(true);
      setMessage(
        "Menu link copied.",
      );

      window.setTimeout(
        () => {
          setCopied(false);
        },
        1800,
      );
    } catch {
      setMessage(
        "Copy was blocked by this browser.",
      );
    }
  }

  async function shareLink() {
    const shareData = {
      title:
        target.tableNumber
          ? `Nova Res Culina — Table ${target.tableNumber}`
          : "Nova Res Culina Menu",
      text:
        target.tableNumber
          ? `Open the Nova Res Culina menu for Table ${target.tableNumber}.`
          : "Open the Nova Res Culina menu.",
      url: target.url,
    };

    try {
      if (
        typeof navigator.share ===
        "function"
      ) {
        await navigator.share(
          shareData,
        );

        setMessage(
          "Menu link shared.",
        );

        return;
      }

      await navigator.clipboard.writeText(
        target.url,
      );

      setMessage(
        "Sharing is not available here, so the link was copied instead.",
      );
    } catch (shareError) {
      if (
        shareError instanceof DOMException &&
        shareError.name ===
          "AbortError"
      ) {
        return;
      }

      setMessage(
        "Could not share the menu link.",
      );
    }
  }

  async function downloadPng() {
    setBusy(true);

    try {
      const png =
        await QRCode.toDataURL(
          target.url,
          {
            width: 1600,
            margin: 3,
            errorCorrectionLevel:
              "H",
            color: {
              dark: "#183d2a",
              light: "#fffdf4",
            },
          },
        );

      downloadDataUrl(
        `${safeFilename(
          target.label,
        )}-qr.png`,
        png,
      );

      setMessage(
        "PNG downloaded.",
      );
    } catch {
      setMessage(
        "Could not generate the PNG.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function downloadSvg() {
    setBusy(true);

    try {
      const svg =
        await QRCode.toString(
          target.url,
          {
            type: "svg",
            width: 1600,
            margin: 3,
            errorCorrectionLevel:
              "H",
            color: {
              dark: "#183d2a",
              light: "#fffdf4",
            },
          },
        );

      downloadBlob(
        `${safeFilename(
          target.label,
        )}-qr.svg`,
        new Blob(
          [svg],
          {
            type: "image/svg+xml;charset=utf-8",
          },
        ),
      );

      setMessage(
        "SVG downloaded.",
      );
    } catch {
      setMessage(
        "Could not generate the SVG.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="border border-[#b5bbae] bg-[#fffdf4] p-5 sm:p-6">
      <div className="grid gap-6 md:grid-cols-[180px_minmax(0,1fr)]">
        <div className="flex items-start justify-center">
          <div className="border border-[#315f3d]/20 bg-white p-3">
            {dataUrl ? (
              <img
                src={dataUrl}
                alt={`${target.label} QR code`}
                className="h-[150px] w-[150px]"
              />
            ) : (
              <div className="flex h-[150px] w-[150px] items-center justify-center text-[#718076]">
                <QrCode
                  size={40}
                  strokeWidth={1.3}
                />
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                {target.tableNumber
                  ? "Table QR"
                  : "General menu"}
              </p>

              <h3 className="mt-1 font-['Cormorant_Garamond'] text-3xl text-[#314a3b]">
                {target.label}
              </h3>

              <p className="mt-2 max-w-lg text-xs leading-5 text-[#667067]">
                {
                  target.description
                }
              </p>
            </div>

            {target.tableNumber && (
              <span className="inline-flex items-center gap-2 rounded-full border border-[#315f3d]/25 bg-[#e7e5d4] px-3 py-2 text-[8px] font-bold uppercase tracking-[0.16em]">
                <UtensilsCrossed
                  size={11}
                />
                Table{" "}
                {
                  target.tableNumber
                }
              </span>
            )}
          </div>

          <div className="mt-5 border border-[#b5bbae]/70 bg-[#f6f1e8] px-4 py-3">
            <p className="text-[8px] font-semibold uppercase tracking-[0.16em] text-[#718076]">
              Permanent menu link
            </p>

            <p className="mt-1 break-all font-mono text-[10px] leading-5 text-[#314a3b]">
              {target.url}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                void copyLink()
              }
              className="inline-flex min-h-10 items-center gap-2 border border-[#315f3d]/30 px-3 text-[8px] font-bold uppercase tracking-[0.13em]"
            >
              {copied ? (
                <Check
                  size={12}
                />
              ) : (
                <Copy
                  size={12}
                />
              )}
              {copied
                ? "Copied"
                : "Copy link"}
            </button>

            <button
              type="button"
              onClick={() =>
                void shareLink()
              }
              className="inline-flex min-h-10 items-center gap-2 border border-[#315f3d]/30 px-3 text-[8px] font-bold uppercase tracking-[0.13em]"
            >
              <Share2
                size={12}
              />
              Share
            </button>

            <button
              type="button"
              onClick={() =>
                window.open(
                  target.url,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="inline-flex min-h-10 items-center gap-2 border border-[#315f3d]/30 px-3 text-[8px] font-bold uppercase tracking-[0.13em]"
            >
              <ExternalLink
                size={12}
              />
              Preview
            </button>

            <button
              type="button"
              disabled={
                busy
              }
              onClick={() =>
                void downloadPng()
              }
              className="inline-flex min-h-10 items-center gap-2 bg-[#314a3b] px-3 text-[8px] font-bold uppercase tracking-[0.13em] text-[#f3ead7] disabled:opacity-40"
            >
              <Download
                size={12}
              />
              PNG
            </button>

            <button
              type="button"
              disabled={
                busy
              }
              onClick={() =>
                void downloadSvg()
              }
              className="inline-flex min-h-10 items-center gap-2 bg-[#9d725c] px-3 text-[8px] font-bold uppercase tracking-[0.13em] text-[#fffdf4] disabled:opacity-40"
            >
              <Download
                size={12}
              />
              SVG
            </button>
          </div>

          {message && (
            <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#667067]">
              {message}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}

export function NovaCmsQrManager({
  onClose,
}: NovaCmsQrManagerProps) {
  const [
    tableCount,
    setTableCount,
  ] =
    useState(12);

  const origin =
    typeof window !==
    "undefined"
      ? window.location.origin
      : "";

  const generalUrl =
    `${origin}/menu`;

  const tableTargets =
    useMemo<QrTarget[]>(
      () =>
        Array.from(
          {
            length:
              tableCount,
          },
          (_, index) => {
            const tableNumber =
              index + 1;

            return {
              id: `table-${tableNumber}`,
              label: `Nova Table ${tableNumber}`,
              description:
                `Guests scanning this code will enter the live menu as Table ${tableNumber}. The table reference stays attached to their QR order.`,
              url:
                `${generalUrl}?t=${tableNumber}`,
              tableNumber,
            };
          },
        ),
      [
        generalUrl,
        tableCount,
      ],
    );

  const generalTarget: QrTarget = {
    id: "general",
    label:
      "Nova General Menu",
    description:
      "Use this QR code for general sharing, takeaway, social media or anywhere a table number is not required.",
    url: generalUrl,
    tableNumber: null,
  };

  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto bg-[#18271e]/80 px-3 py-5 sm:px-5 sm:py-8">
      <div className="mx-auto w-full max-w-6xl border border-[#b5bbae] bg-[#eee9dc] shadow-2xl">
        <header className="sticky top-0 z-10 border-b border-[#f3ead7]/15 bg-[#26392c] px-5 py-5 text-[#f3ead7] sm:px-7">
          <div className="flex items-start justify-between gap-5">
            <div>
              <p className="text-[8px] font-semibold uppercase tracking-[0.24em] text-[#d6bd91]">
                Nova HQ · Menu
              </p>

              <h2 className="mt-1 font-['Cormorant_Garamond'] text-4xl font-light sm:text-5xl">
                QR Menu Control Centre
              </h2>

              <p className="mt-3 max-w-2xl text-xs leading-5 text-[#f3ead7]/65">
                Generate, download,
                preview and share
                Nova&apos;s permanent
                digital menu codes.
              </p>
            </div>

            <button
              type="button"
              onClick={
                onClose
              }
              aria-label="Close QR Menu Control Centre"
              className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#f3ead7]/20"
            >
              <X
                size={16}
              />
            </button>
          </div>
        </header>

        <div className="p-5 sm:p-7">
          <section className="grid gap-4 border border-[#b5bbae] bg-[#f6f1e8] p-5 lg:grid-cols-[1fr_280px] lg:items-end">
            <div>
              <div className="flex items-center gap-2 text-[#9d725c]">
                <Link2
                  size={14}
                />

                <p className="text-[9px] font-semibold uppercase tracking-[0.2em]">
                  Permanent QR system
                </p>
              </div>

              <h3 className="mt-3 font-['Cormorant_Garamond'] text-3xl text-[#314a3b]">
                Print once. Update the
                menu anytime.
              </h3>

              <p className="mt-2 max-w-2xl text-xs leading-5 text-[#667067]">
                The QR contains only the
                Nova menu address. Menu
                items, prices,
                availability and images
                can change without
                reprinting the QR code.
              </p>
            </div>

            <label>
              <span className="text-[8px] font-semibold uppercase tracking-[0.16em] text-[#718076]">
                Number of restaurant
                tables
              </span>

              <input
                type="number"
                min={1}
                max={100}
                value={
                  tableCount
                }
                onChange={(event) =>
                  setTableCount(
                    clampTableCount(
                      Number(
                        event
                          .target
                          .value,
                      ),
                    ),
                  )
                }
                className="mt-2 h-12 w-full border border-[#b5bbae] bg-[#fffdf4] px-4 font-mono text-sm outline-none focus:border-[#314a3b]"
              />
            </label>
          </section>

          <div className="mt-6">
            <QrPreview
              target={
                generalTarget
              }
            />
          </div>

          <section className="mt-10">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[8px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                  Dine-in table codes
                </p>

                <h3 className="mt-1 font-['Cormorant_Garamond'] text-4xl text-[#314a3b]">
                  Tables 1—
                  {tableCount}
                </h3>
              </div>

              <p className="max-w-md text-xs leading-5 text-[#667067]">
                Every table gets its own
                permanent URL and QR.
                Scanning Table 1 opens
                the menu as Table 1 and
                preserves that identity
                through ordering.
              </p>
            </div>

            <div className="grid gap-4">
              {tableTargets.map(
                (target) => (
                  <QrPreview
                    key={
                      target.id
                    }
                    target={
                      target
                    }
                  />
                ),
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
